import BaseFormPresenter from "../../../../base/BaseFormPresenter";
import dialog from "../../../../components/Modal/dialog";
import { indexedDBService } from "../../../../dexie/indexedDBService";
import { getDBObjects } from "../../../../dexie/loadReceipts";
import { upsertUseCase } from "../../../../usecases/object";
import {
  parseGross,
  validateReceiptForSubmission,
} from "../utils/receiptValidations";

class FlexibleFormPagePresenter extends BaseFormPresenter {
  constructor(
    view,
    getObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    findObjectUseCase,
    updateObjectUseCase,
  ) {
    super(
      view,
      getObjectUseCase,
      deleteObjectUseCase,
      upsertUseCase,
      findObjectUseCase,
      updateObjectUseCase,
    );
    this.getObjectUseCase = getObjectUseCase;
    this.deleteObjectUseCase = deleteObjectUseCase;
    this.upsertUseCase = upsertUseCase;
    this.findObjectUseCase = findObjectUseCase;
    this.updateObjectUseCase = updateObjectUseCase;
  }

  componentDidMount() {
    this.getAllBanks();
    this.getAllreceipts();
  }

  async getAllBanks() {
    const collection = "banks";

    try {
      const banks = await this.findObjectUseCase.execute(collection, {});

      this.view.setState({ banks: banks });
    } catch (error) {
      this.view.showError(error);
    }
  }

  async getAllreceipts() {
    const month = this.view.getParamsMonth();
    const year = this.view.getParamsYear();
    const collection = this.view.getIndexedDB();
    try {
      this.view.showProgress();
      const receipts = await getDBObjects(collection, month, year);

      this.view.setState({ receipts: receipts });
      this.view.hideProgress();
    } catch (error) {
      this.view.showError(error);
    }
  }

  async onDelete(object) {
    const { id } = object;
    const collection = this.view.getIndexedDB();
    const { receipts } = this.view.state;
    try {
      dialog.close();
      // this.view.showProgress();

      const object = await indexedDBService.delete(collection, id);

      const updatedReceipts = receipts.filter((r) => r.id !== id);

      this.view.setState({ receipts: updatedReceipts });
      // this.view.hideProgress();
      this.view.successModal("Object Removed Successfully!");
    } catch (error) {
      this.view.showError(error);
    }
  }

  buildUpdatedReceipt(receipt, clientId, user, bankId) {
    const { supplier } = receipt;
    const isVatable = supplier?.isVatable;
    return {
      ...receipt,
      client: { id: clientId || user?.client?.id },
      bank: { id: bankId },
      supplier: { id: receipt.supplier?.id },
      status: isVatable ? "pending" : "non-vat",
    };
  }

  async persistReceiptAndUpdateBank(
    collection,
    indexedDBCollection,
    receipt,
    updatedReceipt,
    bankId,
    grossAmount,
    currentBalance,
  ) {
    const updatedBalance = currentBalance - grossAmount;

    await this.upsertUseCase.execute(collection, updatedReceipt);
    await this.upsertUseCase.execute("banks", {
      id: bankId,
      balance: updatedBalance,
    });
    await indexedDBService.delete(indexedDBCollection, receipt.id);
  }

  showSubmissionResults(upsertedReceipts, failedReceipts) {
    if (failedReceipts.length > 0) {
      const messages = failedReceipts.map(
        (f) => `${f.reason}: ${f.receipt.supplier?.name || "Receipt"}`,
      );
      this.view.showWarning(
        `Some purchases could not be submitted:\n${messages.join("\n")}`,
      );
    }

    if (upsertedReceipts.length > 0) {
      this.view.successModal("Objects Successfully Added!");
      this.view.navigateBack();
    } else if (failedReceipts.length === 0) {
      this.view.navigateBack();
    }
  }

  async submitAllReceipts() {
    const collection = this.view.getCollectionName();
    const indexedDBCollection = this.view.getIndexedDB();
    const { receipts, banks } = this.view.state;
    const clientId = this.view.getClientId();
    const user = this.view.getCurrentUser();

    const bankById = new Map(
      (banks || []).filter((b) => b?.id).map((b) => [b.id, b]),
    );
    const bankBalanceById = new Map(
      (banks || [])
        .filter((b) => b?.id)
        .map((b) => [b.id, parseFloat(b.balance) || 0]),
    );

    try {
      dialog.close();
      this.view.showPageProgress();

      const upsertedReceipts = [];
      const failedReceipts = [];

      for (const receipt of receipts) {
        const bankId = receipt?.bank?.id;
        const bankObj = bankById.get(bankId) ?? null;
        const currentBalance =
          bankBalanceById.get(bankId) ?? (parseFloat(bankObj?.balance) || 0);
        const bankObjWithBalance = bankObj
          ? { ...bankObj, balance: currentBalance }
          : null;

        const grossAmount = parseGross(receipt.gross);

        const validationError = validateReceiptForSubmission(
          receipt,
          bankId,
          bankObjWithBalance,
          grossAmount,
        );
        if (validationError) {
          failedReceipts.push(validationError);
          continue;
        }

        const updatedReceipt = this.buildUpdatedReceipt(
          receipt,
          clientId,
          user,
          bankId,
        );

        await this.persistReceiptAndUpdateBank(
          collection,
          indexedDBCollection,
          receipt,
          updatedReceipt,
          bankId,
          grossAmount,
          currentBalance,
        );

        bankBalanceById.set(bankId, currentBalance - grossAmount);
        upsertedReceipts.push(updatedReceipt);
      }

      const upsertedIds = new Set(upsertedReceipts.map((r) => r.id));
      const remainingReceipts = receipts.filter((r) => !upsertedIds.has(r.id));
      this.view.setState({ receipts: remainingReceipts });

      this.view.hidePageProgress();
      this.showSubmissionResults(upsertedReceipts, failedReceipts);
    } catch (error) {
      this.view.showError(error);
    } finally {
      this.view.hidePageProgress();
    }
  }
}

export default FlexibleFormPagePresenter;
